/**
 * Routage des extraits Kbis vers leur partie (vendeur / acquéreur).
 *
 * Calqué sur `identityRouting.ts` : le rôle est encodé dans la valeur du
 * `CgDocType` (`kbis_vendeur` / `kbis_acheteur`), le classifieur continue de
 * sortir `kbis` brut, et le slot retenu se persiste dans `routed_doc_type`.
 *
 * Pourquoi un routage et pas une convention : le code posait que « le Kbis d'un
 * dossier CTVO est celui de l'ACQUÉREUR ». Mesuré sur la production (26 dossiers
 * avec Kbis depuis juin 2026), c'est faux une fois sur deux — 13 dossiers ont
 * vendeur ET acheteur professionnels, 2 le vendeur seul. Et reg 13548 montre un
 * troisième cas : le Kbis du GARAGE MANDATAIRE, qui n'est ni l'un ni l'autre.
 *
 * ⚠️ Miroir de `packages/backend/src/utils/kbisAttribution.ts` (le monorepo n'a
 * pas de package partagé, comme pour `CgDocType`). Toute évolution des règles
 * d'attribution doit être reportée des deux côtés.
 */
import { CgDocType } from '../ocr/ocrResponseTypes';
import type { UploadedDocResult } from '../store/useFormCGIAStore';

export type KbisPartie = 'vendeur' | 'acheteur';

/** Les deux slots Kbis, plus le type brut conservé pour les dossiers antérieurs. */
export const KBIS_SLOT_TYPES: readonly CgDocType[] = ['kbis_vendeur', 'kbis_acheteur'];

export const isKbisDocType = (docType: string): boolean =>
  docType === 'kbis' || (KBIS_SLOT_TYPES as readonly string[]).includes(docType);

/** Un Kbis du pool, avec son éventuelle partie imposée par la zone de dépôt. */
export interface KbisPoolEntry {
  doc: UploadedDocResult;
  /**
   * Partie imposée par la PROVENANCE du dépôt (zone dédiée « Kbis du vendeur » /
   * « Kbis de l'acquéreur »). Comme `pinnedPerson` du routage d'identité : plus
   * fiable que tout rapprochement, on ne la remet jamais en cause.
   */
  pinnedPartie?: KbisPartie;
}

/** Champs des autres pièces qui identifient chaque partie en propre. */
export interface KbisRefFields {
  /** `vendeur_siret` de la cession. */
  vendeurSiret?: unknown;
  /** `vendeur_raison_sociale` de la cession. */
  vendeurRaisonSociale?: unknown;
  /** `titulaire_siren` du C.1 de la carte grise — le titulaire actuel EST le vendeur. */
  titulaireSiren?: unknown;
  /** `acheteur_siret` de la cession. */
  acheteurSiret?: unknown;
  /** `acheteur_raison_sociale` de la cession. */
  acheteurRaisonSociale?: unknown;
  /** `acheteur_est_personne_morale` / `vendeur_est_personne_morale` de la cession. */
  acheteurEstPersonneMorale?: unknown;
  vendeurEstPersonneMorale?: unknown;
}

const chiffres = (value: unknown): string => String(value ?? '').replace(/\D/g, '');

/** Les 9 premiers chiffres d'un SIRET (14) ou d'un SIREN (9). */
const sirenDe = (value: unknown): string => {
  const nus = chiffres(value);
  return nus.length >= 9 ? nus.slice(0, 9) : '';
};

const FORMES_JURIDIQUES =
  /\b(SARL|SASU|SAS|SCI|SCEA|SNC|SELARL|EURL|EARL|SA|SASP|GAEC|SCOP|SEM|EI|EIRL)\b/g;

/** Raison sociale comparable : majuscules sans accents, sans forme juridique. */
export const normaliseRaisonSociale = (value: unknown): string =>
  String(value ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toUpperCase()
    .replace(FORMES_JURIDIQUES, ' ')
    .replace(/[^A-Z0-9]+/g, ' ')
    .trim();

const memeSociete = (a: unknown, b: unknown): boolean => {
  const na = normaliseRaisonSociale(a);
  const nb = normaliseRaisonSociale(b);
  if (na.length < 3 || nb.length < 3) return false;
  return na === nb || na.includes(nb) || nb.includes(na);
};

/** Champs lus sur le Kbis lui-même. */
const kbisFieldsOf = (doc: UploadedDocResult): { siren: unknown; siret: unknown; raisonSociale: unknown } => {
  const fields = doc.ocrResponse?.fields as Record<string, { value?: unknown }> | undefined;
  return {
    siren: fields?.siren?.value,
    siret: fields?.siret?.value,
    raisonSociale: fields?.raison_sociale?.value,
  };
};

/**
 * À quelle partie ce Kbis appartient-il ? `null` quand rien ne permet de trancher.
 *
 * Les identifiants priment toujours sur les libellés, et le vendeur est testé
 * avant l'acquéreur à chaque niveau : c'est dans ce sens que la convention se
 * trompait.
 */
export const attribuerKbis = (doc: UploadedDocResult, refs: KbisRefFields): KbisPartie | null => {
  const { siren, siret, raisonSociale } = kbisFieldsOf(doc);
  const sirenKbis = sirenDe(siren) || sirenDe(siret);

  if (sirenKbis) {
    if (sirenKbis === sirenDe(refs.vendeurSiret) || sirenKbis === sirenDe(refs.titulaireSiren)) return 'vendeur';
    if (sirenKbis === sirenDe(refs.acheteurSiret)) return 'acheteur';
  }

  if (memeSociete(raisonSociale, refs.vendeurRaisonSociale)) return 'vendeur';
  if (memeSociete(raisonSociale, refs.acheteurRaisonSociale)) return 'acheteur';

  const acheteurPM = refs.acheteurEstPersonneMorale === true;
  const vendeurPM = refs.vendeurEstPersonneMorale === true;
  if (acheteurPM && !vendeurPM) return 'acheteur';
  if (vendeurPM && !acheteurPM) return 'vendeur';

  return null;
};

export interface KbisRoutingResult {
  /** Slot assigné par pièce : clé = URL Firebase (identifiant stable du pool). */
  slotByUrl: Record<string, CgDocType>;
  /** Documents par slot, docType réécrit au slot assigné. */
  slots: Partial<Record<CgDocType, UploadedDocResult>>;
  /**
   * Pièces placées SANS preuve : aucun identifiant ni raison sociale ne les
   * rattachait, et le slot acquéreur était libre. L'assignation est faite (il
   * faut bien afficher la pièce) mais relève du repli, pas d'une preuve — au
   * relecteur de trancher.
   */
  ambiguousUrls: string[];
}

const urlOf = (doc: UploadedDocResult): string => doc.firebaseUrls?.[0] ?? '';

/**
 * Route TOUS les Kbis du pool vers leurs slots. Recalculé intégralement à chaque
 * appel : le résultat ne dépend que du pool et des pièces de référence, jamais
 * de l'ordre d'arrivée.
 *
 * Ordre des règles :
 *  1. Partie imposée par la zone de dépôt (`pinnedPartie`) — jamais remise en cause.
 *  2. Rapprochement des identifiants, puis des raisons sociales (`attribuerKbis`).
 *  3. Repli : la première partie dont le slot est encore libre, acquéreur d'abord
 *     (convention historique), la pièce étant alors marquée ambiguë.
 *
 * Une pièce SANS preuve dont le slot voulu est déjà pris ne l'écrase pas : elle
 * bascule sur l'autre slot s'il est libre, sinon reste non assignée — mieux vaut
 * une pièce visible en « autres documents » qu'une société écrasée.
 *
 * Une pièce PROUVÉE dont le slot est déjà pris (second extrait Kbis de la même
 * société) reste non assignée : elle ne passe jamais au repli, qui la rangerait
 * chez l'autre partie. EJ-509-YJ (2026-10-28) : deux Kbis de l'acquéreur, le
 * second atterrissait en Kbis du vendeur et sa raison sociale remplaçait celle
 * du vrai vendeur.
 */
export const routeKbisDocs = (pool: readonly KbisPoolEntry[], refs: KbisRefFields): KbisRoutingResult => {
  const slotByUrl: Record<string, CgDocType> = {};
  const slots: Partial<Record<CgDocType, UploadedDocResult>> = {};
  const ambiguousUrls: string[] = [];
  /** Slots attribués sur preuve : non écrasables par un repli. */
  const prouve = new Set<CgDocType>();

  const slotDe = (partie: KbisPartie): CgDocType => (partie === 'vendeur' ? 'kbis_vendeur' : 'kbis_acheteur');

  const assigner = (entry: KbisPoolEntry, partie: KbisPartie, avecPreuve: boolean): boolean => {
    const slot = slotDe(partie);
    if (slots[slot] && (prouve.has(slot) || !avecPreuve)) return false;
    const url = urlOf(entry.doc);
    if (!url) return false;
    slots[slot] = { ...entry.doc, docType: slot };
    slotByUrl[url] = slot;
    if (avecPreuve) prouve.add(slot);
    else ambiguousUrls.push(url);
    return true;
  };

  // 1 & 2 — la zone de dépôt, puis les identifiants. Les deux valent preuve.
  // Une pièce rattachée à une partie dont le slot est déjà pris n'entre PAS au
  // repli : elle appartient à cette partie, pas à l'autre.
  const sansPreuve: KbisPoolEntry[] = [];
  for (const entry of pool) {
    const partie = entry.pinnedPartie ?? attribuerKbis(entry.doc, refs);
    if (!partie) sansPreuve.push(entry);
    else assigner(entry, partie, true);
  }

  // 3 — repli sur le premier slot libre, acquéreur d'abord.
  for (const entry of sansPreuve) {
    if (!assigner(entry, 'acheteur', false)) assigner(entry, 'vendeur', false);
  }

  return { slotByUrl, slots, ambiguousUrls };
};
